"use client"

import type React from "react"
import { useEffect, useState, useRef } from "react"
import { useParams } from "react-router-dom"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import axiosInstance from "../utils/axiosConfig.ts"
import { getAssetUrl } from "../utils/auth.ts"
import MBALogo from "../assets/mba_logo.tsx"
import { PaidStamp } from "../components/PaidStamp.tsx"
import { InvoicePaymentMethodCard } from "../components/invoice/InvoicePaymentMethodCard.tsx"
import { InvoiceLineItems } from "../components/invoice/InvoiceLineItems.tsx"
import { InvoiceReferralDiscounts } from "../components/invoice/InvoiceReferralDiscounts.tsx"
import type { ReferralDiscountRow } from "../components/invoice/referralDiscountUtils.ts"
import { InvoicePaymentSummary } from "../components/invoice/InvoicePaymentSummary.tsx"
import { InvoiceStatusBadge } from "../components/invoice/InvoiceStatusBadge.tsx"
import {
  formatInvoiceDate,
  getPaymentStatusColor,
  isSubscriptionInvoice,
} from "../components/invoice/invoiceUtils.ts"

interface LineItem {
  id: string
  description: string
  quantity: number
  unit_price: number
  discount_amount: number
  line_total: number
  charge_type?: string
  item_type?: string
  inventory_item_id?: string
  inventory_item_type?: string
  billing_start_date?: string | null
  billing_end_date?: string | null
}

interface PendingInvoice {
  id: string
  invoice_number: string
  billing_start_date: string
  billing_end_date: string
  due_date: string
  total_amount: number
  paid_amount: number
  remaining_amount: number
  status: string
  invoice_type: string
}

interface PendingInvoicesData {
  count: number
  total_pending_amount: number
  invoices: PendingInvoice[]
}

interface CompanyInfo {
  id: string
  name: string
  address?: string
  contact_number?: string
  email?: string
  tagline?: string
  tax_number?: string
  invoice_footer_notes?: string
  logo_url?: string | null
}

interface InvoiceData {
  id: string
  invoice_number: string
  customer_name: string
  customer_address: string
  customer_internet_id: string
  customer_phone: string
  service_plan_name: string
  billing_start_date: string
  billing_end_date: string
  due_date: string
  subtotal: number
  discount_percentage: number
  total_amount: number
  pre_referral_total?: number
  referral_discount_total?: number
  referral_discounts?: ReferralDiscountRow[]
  invoice_type: string
  notes: string
  status: string
  payments: PaymentDetails[]
  total_paid: number
  remaining_amount: number
  line_items?: LineItem[]
  pending_invoices?: PendingInvoicesData
  company?: CompanyInfo
}

interface PaymentDetails {
  id: string
  amount: number
  payment_date: string
  payment_method: string
  transaction_id?: string
  status: string
  failure_reason?: string
}

interface BankAccount {
  id: string
  bank_name: string
  account_title: string
  account_number: string
  iban?: string
  branch_code?: string
  qr_code_image?: string | null
}

const PublicInvoicePage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [invoiceData, setInvoiceData] = useState<InvoiceData | null>(null)
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([])
  const [isDownloading, setIsDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const printRef = useRef<HTMLDivElement>(null)
  const paymentFormRef = useRef<HTMLDivElement>(null)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submissionSuccess, setSubmissionSuccess] = useState(false)
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'Bank Transfer',
    transaction_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    bank_account_id: '',
    payment_proof: null as File | null
  })

  useEffect(() => {
    if (invoiceData?.remaining_amount) {
      setPaymentForm(prev => ({ ...prev, amount: invoiceData.remaining_amount.toString() }))
    }
  }, [invoiceData])

  useEffect(() => {
    if (!id) return
    fetchInvoiceData()
    fetchBankAccounts()
  }, [id])

  useEffect(() => {
    const compName = invoiceData?.company?.name || 'MBA NET'
    document.title = `Invoice | ${compName}`
  }, [invoiceData?.company?.name])

  const fetchInvoiceData = async () => {
    try {
      const response = await axiosInstance.get(`/public/invoice/${id}`)
      setInvoiceData(response.data)
      setError(null)
    } catch (error) {
      console.error("Failed to fetch invoice data", error)
      setError("Failed to load invoice data. Please check the link.")
    }
  }

  const fetchBankAccounts = async () => {
    if (!id) return
    try {
      const response = await axiosInstance.get(`/public/bank-accounts/list?invoice_id=${id}`)
      setBankAccounts(response.data)
    } catch (error) {
      console.error("Failed to fetch bank accounts", error)
    }
  }

  const formatDate = formatInvoiceDate

  const isSubscription = isSubscriptionInvoice(invoiceData?.invoice_type, invoiceData?.line_items)
  const issueDate = invoiceData?.billing_start_date || invoiceData?.due_date

  const handleDownloadPDF = async () => {
    if (!printRef.current) return
    setIsDownloading(true)
    setError(null)
    try {
      const canvas = await html2canvas(printRef.current, {
        scale: 2, useCORS: true, allowTaint: true, backgroundColor: "#ffffff",
        width: printRef.current.scrollWidth, height: printRef.current.scrollHeight,
      })
      const imgData = canvas.toDataURL("image/jpeg", 0.8)
      const pdf = new jsPDF("p", "mm", "a4")
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const imgWidth = pdfWidth
      const imgHeight = (canvas.height * pdfWidth) / canvas.width
      let heightLeft = imgHeight
      let position = 0
      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight)
      heightLeft -= pdfHeight
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight
        pdf.addPage()
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight)
        heightLeft -= pdfHeight
      }
      pdf.save(`Invoice-${invoiceData?.invoice_number}.pdf`)
    } catch (error) {
      console.error("PDF generation failed:", error)
      setError("Failed to generate PDF. Please try again.")
    } finally {
      setIsDownloading(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setPaymentForm({ ...paymentForm, payment_proof: e.target.files[0] })
    }
  }

  const scrollToPaymentForm = () => {
    paymentFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)
    if (!paymentForm.payment_proof) {
      setError("Please upload a payment proof (screenshot/image)")
      setIsSubmitting(false)
      return
    }
    if (paymentForm.payment_method === 'Bank Transfer' && !paymentForm.bank_account_id) {
      setError("Please select a bank account")
      setIsSubmitting(false)
      return
    }
    try {
      const formData = new FormData()
      formData.append('invoice_id', invoiceData?.id || id!)
      formData.append('amount', paymentForm.amount)
      formData.append('payment_method', paymentForm.payment_method)
      if (paymentForm.bank_account_id) formData.append('bank_account_id', paymentForm.bank_account_id)
      formData.append('transaction_id', paymentForm.transaction_id)
      formData.append('payment_date', paymentForm.payment_date)
      formData.append('payment_proof', paymentForm.payment_proof)
      await axiosInstance.post('/public/payment/submit', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      setSubmissionSuccess(true)
      fetchInvoiceData()
      setPaymentForm(prev => ({ ...prev, transaction_id: '', payment_proof: null }))
    } catch (err: any) {
      console.error("Payment submission failed:", err)
      setError(err.response?.data?.message || "Failed to submit payment. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  // Loading State
  if (!invoiceData && !error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-light-sky/50">
        <div className="text-center">
          <div className="relative mx-auto mb-4 h-10 w-10">
            <div className="absolute inset-0 animate-spin rounded-full border-2 border-electric-blue border-t-transparent" />
          </div>
          <p className="text-sm font-medium text-slate-gray">Loading invoice...</p>
        </div>
      </div>
    )
  }

  // Error State
  if (error && !invoiceData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-light-sky/50 px-4">
        <div className="max-w-md rounded-xl border border-red-100 bg-white p-6 text-center shadow-sm">
          <h2 className="mb-2 text-lg font-semibold text-slate-800">Invoice Not Found</h2>
          <p className="mb-4 text-sm text-slate-600">{error}</p>
          <button onClick={fetchInvoiceData} className="h-9 rounded-lg bg-electric-blue px-4 text-sm font-medium text-white hover:bg-btn-hover">
            Try Again
          </button>
        </div>
      </div>
    )
  }

  const hasPendingPayment = invoiceData?.payments?.some((p) => p.status === "pending")
  const showPaymentForm =
    (invoiceData?.remaining_amount || 0) > 0 && !submissionSuccess && !hasPendingPayment
  const showStickyPayCta = showPaymentForm

  return (
    <div className="min-h-screen bg-light-sky/50 px-3 pb-24 pt-6 sm:px-4 sm:pb-8 sm:pt-8">
      <div className="mx-auto max-w-4xl">
        {/* Public header */}
        <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            {invoiceData?.company?.logo_url ? (
              <img
                src={getAssetUrl(invoiceData.company.logo_url)!}
                alt={invoiceData.company.name}
                className="mb-2 max-h-8 max-w-[120px] object-contain sm:max-h-10"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none" }}
              />
            ) : null}
            <h1 className="text-lg font-semibold text-deep-ocean sm:text-xl">
              {invoiceData?.company?.name || "Invoice"}
            </h1>
            <p className="mt-0.5 text-xs text-slate-gray sm:text-sm">
              Invoice #{invoiceData?.invoice_number}
              {invoiceData?.due_date && <> · Due {formatDate(invoiceData.due_date)}</>}
            </p>
          </div>
          <button
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            className={`flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:border-electric-blue hover:text-electric-blue ${isDownloading ? "cursor-not-allowed opacity-50" : ""}`}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {isDownloading ? "Generating..." : "Download PDF"}
          </button>
        </div>

        {/* Main Invoice Card */}
        <div className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
          <div ref={printRef} className="p-4 sm:p-8">

            {/* Header Section */}
            <div className="flex flex-col gap-5 border-b border-slate-100 pb-6 sm:grid sm:grid-cols-[minmax(0,1fr)_8rem_minmax(12rem,auto)] sm:items-start sm:gap-6 sm:pb-8">
              <div className="min-w-0 flex-1">
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                  <span className="text-xl font-bold text-deep-ocean sm:text-2xl">INVOICE</span>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <InvoiceStatusBadge status={invoiceData?.status || ""} />
                    {invoiceData?.invoice_type && (
                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-600">
                        {invoiceData.invoice_type.split("_").join(" ")}
                      </span>
                    )}
                  </div>
                </div>
                <p className="mt-1 font-mono text-sm text-slate-500">#{invoiceData?.invoice_number}</p>
              </div>
              <div className="flex min-h-[6.5rem] items-center justify-start sm:justify-center">
                {invoiceData?.status === "paid" && (
                  <PaidStamp
                    date={invoiceData.payments?.[invoiceData.payments.length - 1]?.payment_date}
                    method={invoiceData.payments?.[invoiceData.payments.length - 1]?.payment_method}
                    className="h-20 w-20 text-emerald-600 border-emerald-600 sm:h-28 sm:w-28"
                  />
                )}
              </div>
              <div className="flex w-full min-w-0 flex-col items-start text-left sm:w-auto sm:items-end sm:text-right">
                <div className="mb-2 inline-flex h-10 items-center justify-start gap-2 sm:mb-3 sm:h-12 sm:justify-end sm:gap-3">
                  {invoiceData?.company?.logo_url ? (
                    <img
                      src={getAssetUrl(invoiceData.company.logo_url)!}
                      alt={invoiceData.company.name}
                      className="max-h-10 max-w-[120px] object-contain sm:max-h-12 sm:max-w-[140px]"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none" }}
                    />
                  ) : (
                    <div className="flex h-8 w-24 items-center sm:h-10 sm:w-28">
                      <MBALogo variant="landscape" companyName={invoiceData?.company?.name} />
                    </div>
                  )}
                </div>
                <p className="font-bold text-[#2A5C8A] text-base">{invoiceData?.company?.name || ""}</p>
                {invoiceData?.company?.address && (
                  <p className="text-slate-500 text-xs leading-relaxed whitespace-pre-line">
                    {invoiceData.company.address}
                  </p>
                )}
                {invoiceData?.company?.contact_number && (
                  <p className="text-slate-500 text-xs">{invoiceData.company.contact_number}</p>
                )}
                {invoiceData?.company?.tax_number && (
                  <p className="text-slate-500 text-xs font-mono">Tax Reg: {invoiceData.company.tax_number}</p>
                )}
              </div>
            </div>

            {/* Customer & Invoice Meta Grid */}
            <div className="grid gap-4 border-b border-slate-100 py-6 sm:gap-6 sm:py-8 md:grid-cols-2">
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 sm:p-5">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">Bill To</p>
                <p className="mb-1 text-base font-bold text-slate-800 sm:text-lg">{invoiceData?.customer_name}</p>
                <div className="space-y-1 text-sm text-slate-600">
                  <p><span className="text-slate-400">ID:</span> {invoiceData?.customer_internet_id}</p>
                  <p><span className="text-slate-400">Phone:</span> {invoiceData?.customer_phone}</p>
                  <p className="text-slate-500">{invoiceData?.customer_address}</p>
                </div>
              </div>
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 sm:p-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Issue Date</p>
                    <p className="text-sm font-semibold text-slate-700">{issueDate && formatDate(issueDate)}</p>
                  </div>
                  <div>
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Due Date</p>
                    <p className="text-sm font-semibold text-slate-700">{invoiceData?.due_date && formatDate(invoiceData.due_date)}</p>
                  </div>
                </div>
                {isSubscription && (
                  <div className="mt-4 border-t border-slate-200/50 pt-4">
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Billing Period</p>
                    <p className="text-sm font-semibold text-slate-700">
                      {invoiceData?.billing_start_date && formatDate(invoiceData.billing_start_date)} — {invoiceData?.billing_end_date && formatDate(invoiceData.billing_end_date)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <InvoiceLineItems
              lineItems={invoiceData?.line_items}
              invoiceType={invoiceData?.invoice_type}
              servicePlanName={invoiceData?.service_plan_name || ""}
              subtotal={invoiceData?.subtotal ?? 0}
              discountPercentage={invoiceData?.discount_percentage ?? 0}
              totalAmount={invoiceData?.pre_referral_total ?? invoiceData?.total_amount ?? 0}
              totalPaid={invoiceData?.total_paid ?? 0}
              remainingAmount={invoiceData?.remaining_amount ?? 0}
              isSubscription={isSubscription}
              billingStartDate={invoiceData?.billing_start_date}
              billingEndDate={invoiceData?.billing_end_date}
              issueDate={issueDate}
            />

            <InvoiceReferralDiscounts
              rows={invoiceData?.referral_discounts}
              referralDiscountTotal={invoiceData?.referral_discount_total ?? 0}
              preReferralTotal={invoiceData?.pre_referral_total}
            />

            <InvoicePaymentSummary
              totalAmount={invoiceData?.total_amount ?? 0}
              totalPaid={invoiceData?.total_paid ?? 0}
              remainingAmount={invoiceData?.remaining_amount ?? 0}
            />

            {/* Payment History */}
            {invoiceData?.payments && invoiceData.payments.length > 0 && (
              <div className="border-b border-slate-100 py-6 sm:py-8">
                <p className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">Payment History</p>
                <div className="space-y-3">
                  {invoiceData.payments.map((payment, index) => (
                    <div key={payment.id} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-800">Payment #{invoiceData.payments.length - index}</p>
                          <p className="text-xs text-slate-500">{formatDate(payment.payment_date)} • {payment.payment_method}</p>
                          {payment.transaction_id && <p className="mt-1 truncate text-xs text-slate-400">Ref: {payment.transaction_id}</p>}
                        </div>
                        <div className="flex items-center justify-between gap-3 sm:flex-col sm:items-end">
                          <p className="text-base font-bold tabular-nums text-slate-800 sm:text-lg">PKR {payment.amount.toLocaleString()}</p>
                          <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${getPaymentStatusColor(payment.status)}`}>
                            {payment.status}
                          </span>
                        </div>
                      </div>
                      {payment.failure_reason && payment.status === "failed" && (
                        <p className="mt-2 rounded-lg border border-red-100 bg-red-50 p-2 text-xs text-red-600">{payment.failure_reason}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending Payment Alert — hidden when form is visible */}
            {(invoiceData?.remaining_amount || 0) > 0 && !showPaymentForm && (
              <div className="my-4 rounded-xl border border-amber-200 bg-amber-50 p-4 print:hidden sm:my-6 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100">
                    <svg className="h-4 w-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold text-amber-800">Payment Required</p>
                    <p className="text-sm text-amber-700">
                      Outstanding balance of <strong>PKR {invoiceData?.remaining_amount.toLocaleString()}</strong> is due.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {invoiceData?.pending_invoices && invoiceData.pending_invoices.count > 0 && (
              <div className="my-4 rounded-xl border border-red-200 bg-red-50 p-4 print:hidden sm:my-6 sm:p-5">
                <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <p className="font-bold text-red-800">
                    Other Outstanding Invoices ({invoiceData.pending_invoices.count})
                  </p>
                  <span className="text-sm font-bold tabular-nums text-red-700">
                    Total: PKR {invoiceData.pending_invoices.total_pending_amount.toLocaleString()}
                  </span>
                </div>
                <div className="custom-scrollbar max-h-40 space-y-2 overflow-y-auto">
                  {invoiceData.pending_invoices.invoices.map((inv) => (
                    <div key={inv.id} className="flex flex-col gap-1 rounded-lg border border-red-100 bg-white px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
                        <span className="truncate font-mono font-semibold text-slate-700">#{inv.invoice_number}</span>
                        <span className="text-xs text-slate-400">{formatDate(inv.billing_start_date)}</span>
                      </div>
                      <span className="font-bold tabular-nums text-red-600">PKR {inv.remaining_amount.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {bankAccounts.length > 0 && (
              <div className="border-b border-slate-100 py-6 sm:py-8">
                <p className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">Payment Methods</p>
                <div className="grid gap-4 md:grid-cols-2">
                  {bankAccounts.map((account) => (
                    <InvoicePaymentMethodCard key={account.id} account={account} />
                  ))}
                </div>
              </div>
            )}

            {/* Submit Payment Form */}
            {(invoiceData?.remaining_amount || 0) > 0 && hasPendingPayment && !submissionSuccess && (
              <div className="border-b border-slate-100 py-6 sm:py-8">
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 sm:p-6">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
                      <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-base font-bold text-amber-800 sm:text-lg">Payment Pending Verification</p>
                      <p className="text-sm text-amber-700">
                        You have already submitted a payment that is awaiting verification.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showPaymentForm && (
              <div ref={paymentFormRef} className="border-b border-slate-100 py-6 sm:py-8 scroll-mt-20">
                <p className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">Submit Payment Proof</p>
                <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4 sm:p-6">
                  <form onSubmit={handlePaymentSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Amount (PKR)</label>
                        <input
                          type="number"
                          step="0.01"
                          required
                          value={paymentForm.amount}
                          onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                          className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-electric-blue"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Bank Account</label>
                        <select
                          value={paymentForm.bank_account_id}
                          onChange={(e) => setPaymentForm({ ...paymentForm, bank_account_id: e.target.value })}
                          required
                          className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-electric-blue"
                        >
                          <option value="">Select Bank Account</option>
                          {bankAccounts.map((account) => (
                            <option key={account.id} value={account.id}>
                              {account.bank_name} - {account.account_number}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Transaction ID</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. TRX-123456789"
                          value={paymentForm.transaction_id}
                          onChange={(e) => setPaymentForm({ ...paymentForm, transaction_id: e.target.value })}
                          className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-electric-blue"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Payment Date</label>
                        <input
                          type="date"
                          required
                          value={paymentForm.payment_date}
                          onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                          className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-electric-blue"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Payment Proof (Required)</label>
                      <input
                        type="file"
                        required
                        accept="image/*,application/pdf"
                        onChange={handleFileChange}
                        className="w-full text-sm text-slate-500 file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-electric-blue file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-btn-hover"
                      />
                    </div>
                    {error && (
                      <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">{error}</p>
                    )}
                    <div className="flex justify-stretch sm:justify-end">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`h-10 w-full rounded-lg bg-electric-blue px-4 text-sm font-semibold text-white hover:bg-btn-hover sm:w-auto sm:min-w-[160px] ${isSubmitting ? "cursor-not-allowed opacity-50" : ""}`}
                      >
                        {isSubmitting ? "Submitting..." : "Submit Payment"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {submissionSuccess && (
              <div className="my-6 bg-emerald-50 rounded-xl p-6 border border-emerald-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-emerald-800">Payment Submitted!</p>
                    <p className="text-emerald-700">Your payment is pending verification. Status will update once approved.</p>
                  </div>
                </div>
                <button onClick={() => setSubmissionSuccess(false)} className="mt-4 text-sm font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-800">
                  Submit another payment
                </button>
              </div>
            )}

            {/* Notes */}
            {invoiceData?.notes && (
              <div className="py-6">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Notes</p>
                <p className="text-sm text-slate-600 bg-slate-50 rounded-xl p-4 border border-slate-100">{invoiceData.notes}</p>
              </div>
            )}

            {/* Footer */}
            <div className="mt-6 border-t border-slate-100 pt-6 text-center sm:mt-8 sm:pt-8">
              {invoiceData?.company?.invoice_footer_notes && (
                <p className="text-xs text-slate-600 my-2 max-w-xl mx-auto whitespace-pre-line bg-slate-50 p-3 rounded-lg border border-slate-100">
                  {invoiceData.company.invoice_footer_notes}
                </p>
              )}
              {(invoiceData?.company?.email || invoiceData?.company?.contact_number) && (
                <p className="text-sm text-slate-500 mb-2">
                  Questions? {invoiceData.company.email && (
                    <a href={`mailto:${invoiceData.company.email}`} className="text-blue-600 hover:underline">{invoiceData.company.email}</a>
                  )}
                  {invoiceData.company.contact_number && (
                    <span>{invoiceData.company.email ? ' • ' : ''}{invoiceData.company.contact_number}</span>
                  )}
                </p>
              )}
              <p className="text-xs text-slate-400">Invoice generated on {formatDate(new Date().toISOString())}</p>
            </div>
          </div>
        </div>
      </div>

      {showStickyPayCta && (
        <div className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur-sm sm:hidden">
          <div className="mx-auto flex max-w-4xl items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] text-slate-500">Balance due</p>
              <p className="text-lg font-bold tabular-nums text-red-700">
                PKR {invoiceData?.remaining_amount.toLocaleString()}
              </p>
            </div>
            <button
              type="button"
              onClick={scrollToPaymentForm}
              className="h-10 shrink-0 rounded-lg bg-electric-blue px-4 text-sm font-semibold text-white hover:bg-btn-hover"
            >
              Submit payment
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default PublicInvoicePage
